import { Label, Text, TextSize, TextWeight } from '@vizit/atoms';
import { CheckIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { Radio, RadioGroup } from 'react-aria-components';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

const IMAGE_TYPE_OPTIONS = [
  { value: 'hero', label: 'Hero' },
  { value: 'carousel', label: 'Carousel' },
] as const;

interface ImageTypeFieldProps<T extends FieldValues> {
  /** The control object from the parent `useForm` hook. */
  control: Control<T>;
  /** The name of the image-type field in the parent form. */
  name: Path<T>;
  /** When true, the "Hero" option is omitted, leaving only "Carousel". */
  hideHeroOption?: boolean;
  /** When true, the label shows a required marker and the group is announced as required. */
  isRequired?: boolean;
}

/**
 * Image-type selector rendered as two side-by-side radio cards (Hero / Carousel),
 * matching the design. Single-select, wired to react-hook-form via `control`/`name`.
 */
function ImageTypeField<T extends FieldValues>(props: ImageTypeFieldProps<T>) {
  const { control, name, hideHeroOption = false, isRequired = false } = props;
  const { field, fieldState } = useController({ name, control });

  const options = hideHeroOption ? IMAGE_TYPE_OPTIONS.filter((option) => option.value !== 'hero') : IMAGE_TYPE_OPTIONS;

  return (
    <RadioGroup
      aria-label="Image type"
      isRequired={isRequired}
      value={field.value || null}
      onBlur={field.onBlur}
      onChange={field.onChange}
      isInvalid={!!fieldState.error}
      className="flex flex-col gap-4"
    >
      <Label isRequired={isRequired}>Image type</Label>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <Radio
            key={option.value}
            value={option.value}
            data-testid={`image-type-${option.value}`}
            className={({ isSelected, isFocusVisible }) =>
              cx(
                'flex cursor-pointer items-center gap-6 rounded-none border p-6 transition-colors',
                isSelected ? 'border-brand-600' : 'border-primary',
                isFocusVisible && 'outline outline-2 outline-offset-2 outline-brand-600',
              )
            }
          >
            {({ isSelected }) => (
              <>
                <span
                  className={cx(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-none border',
                    isSelected ? 'border-brand-600 bg-brand-solid text-white' : 'border-primary bg-primary',
                  )}
                >
                  {isSelected && <CheckIcon size={20} />}
                </span>
                <Text size={TextSize.Md} weight={TextWeight.Medium} className="text-secondary">
                  {option.label}
                </Text>
              </>
            )}
          </Radio>
        ))}
      </div>
      {fieldState.error?.message && (
        <Text size={TextSize.Sm} className="text-error-primary">
          {fieldState.error.message}
        </Text>
      )}
    </RadioGroup>
  );
}

export { ImageTypeField };
export type { ImageTypeFieldProps };
